/**
 * Живые события CRM → правка загруженного списка сделок «Воронки» на месте.
 *
 * Что можно применить точно — применяем сразу (новая стадия, сумма, ответственный, удаление,
 * переезд в другую воронку). Итоги этапов, счётчики и попадание в отбор («мои», «с пропущенными»)
 * по событию не посчитать — для них ответ `refetch: true`: экран перечитает список с задержкой,
 * склеивая пачку событий в один запрос. Чистая функция — проверяется отдельно.
 */
import type { DealListResponse, DealRowData } from '@/api/pipeline';
import { RESYNC, type CrmEvent } from './crmEvents';

export interface DealPages {
  pages: DealListResponse[];
  pageParams: unknown[];
}

export interface ReduceResult {
  list: DealPages | undefined;
  /** Перечитать список (итоги, отбор, новая сделка). */
  refetch: boolean;
}

/** Поля сделки, которые событие может прислать и которые видны в строке. */
const ROW_FIELDS: Array<keyof DealRowData> = [
  'title', 'amount', 'currency', 'stageId', 'pipelineId', 'ownerId', 'closedAt', 'awaitingReplySince',
  'chatwootAccountId', 'chatwootDisplayId', 'updatedAt',
];
const ROW_OBJECTS: Array<keyof DealRowData> = ['stage', 'company', 'person'];

function idOf(ev: CrmEvent): string | null {
  const p = ev.payload as Record<string, unknown> | null;
  const id = p?.id ?? p?.dealId;
  return typeof id === 'string' ? id : null;
}

function hasRow(list: DealPages | undefined, id: string): boolean {
  return !!list?.pages.some((pg) => pg.data.some((d) => d.id === id));
}

function mapRows(list: DealPages, fn: (d: DealRowData) => DealRowData | null): DealPages {
  return {
    ...list,
    pages: list.pages.map((pg) => {
      let removed = 0;
      const data: DealRowData[] = [];
      for (const d of pg.data) {
        const next = fn(d);
        if (next) data.push(next);
        else removed++;
      }
      return removed ? { ...pg, data, total: Math.max(0, pg.total - removed) } : (data.some((d, i) => d !== pg.data[i]) ? { ...pg, data } : pg);
    }),
  };
}

/** Слить присланные поля в строку. Вложенные объекты — только если пришли объектом (не id). */
export function mergeDeal(row: DealRowData, payload: Record<string, unknown>): DealRowData {
  const next: Record<string, unknown> = { ...row };
  for (const k of ROW_FIELDS) if (k in payload) next[k] = payload[k];
  for (const k of ROW_OBJECTS) {
    const v = payload[k];
    if (v && typeof v === 'object') next[k] = v;
  }
  // Сменилась стадия, а объект стадии не пришёл — старый объект врал бы цветом.
  if ('stageId' in payload && !(payload.stage && typeof payload.stage === 'object') && row.stage?.id !== payload.stageId) {
    next.stage = null;
  }
  return next as unknown as DealRowData;
}

export function applyDealEvent(list: DealPages | undefined, ev: CrmEvent, ctx: { pipelineId: string }): ReduceResult {
  if (ev.type === RESYNC) return { list, refetch: true };
  const p = (ev.payload ?? {}) as Record<string, unknown>;
  const id = idOf(ev);
  const present = !!id && hasRow(list, id);
  const ours = p.pipelineId === ctx.pipelineId;

  switch (ev.type) {
    case 'deal.deleted': {
      if (!present || !list) return { list, refetch: false };
      return { list: mapRows(list, (d) => (d.id === id ? null : d)), refetch: true };
    }
    case 'deal.created':
      return { list, refetch: ours };
    case 'deal.updated':
    case 'deal.moved':
    case 'deal.changed': {
      if (!present || !list) return { list, refetch: ours };
      // Уехала в другую воронку — из списка этой убираем.
      if (typeof p.pipelineId === 'string' && p.pipelineId !== ctx.pipelineId) {
        return { list: mapRows(list, (d) => (d.id === id ? null : d)), refetch: true };
      }
      const patch: Record<string, unknown> = { ...p };
      // deal.moved: toStageId — куда; deal.changed: stageId без объекта стадии.
      if (typeof p.toStageId === 'string') patch.stageId = p.toStageId;
      delete patch.id;
      delete patch.dealId;
      return { list: mapRows(list, (d) => (d.id === id ? mergeDeal(d, patch) : d)), refetch: true };
    }
    case 'call.changed': {
      // Звонок по сделке из списка: пропущенные пересчитает сервер.
      const dealId = typeof p.dealId === 'string' ? p.dealId : null;
      return { list, refetch: !!dealId && hasRow(list, dealId) };
    }
    default:
      return { list, refetch: false };
  }
}
